import type {
  AtendenteElegivel,
  EntradaAtualizarEquipe,
  EntradaAtualizarUnidade,
  EntradaAtualizarUsuario,
  EntradaCriarEquipe,
  EntradaCriarUnidade,
  EntradaCriarUsuario,
  EntradaDesativarUsuario,
  EntradaTransferirCarteira,
  EnvelopeOffset,
  Equipe,
  MembroEquipe,
  Papel,
  ResultadoDesativacaoUsuario,
  ResultadoTransferenciaDeCarteira,
  Unidade,
  UsuarioDetalhado,
  UsuarioNaLista,
} from '@baiocchi/shared'

import { api, type ParametrosConsulta } from './cliente'

/**
 * `/unidades`, `/equipes` e `/usuarios` (`03-endpoints-draft.md §2 e §3`).
 *
 * Os tipos vêm de `@baiocchi/shared`: os módulos existem em `apps/api` e validam com os
 * **mesmos** schemas Zod (RNF-121). Na 1ª rodada estes contratos moravam em
 * `contratos-pendentes.ts`, porque o backend não existia — INT-16 resolvido.
 *
 * Listagens desta família são paginadas por **offset** (`EnvelopeOffset`), a exceção
 * documentada em §3: volume pequeno e a interface mostra numeração.
 */
export const unidadesApi = {
  listar(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeOffset<Unidade>>('/unidades', filtros, sinal)
  },

  detalhar(id: number, sinal?: AbortSignal) {
    return api.get<Unidade>(`/unidades/${id}`, undefined, sinal)
  },

  criar(entrada: EntradaCriarUnidade) {
    return api.post<Unidade>('/unidades', entrada)
  },

  atualizar(id: number, entrada: EntradaAtualizarUnidade) {
    return api.patch<Unidade>(`/unidades/${id}`, entrada)
  },

  /**
   * Não existe `DELETE`: unidade em uso é desativada (RN-41). Responde `409
   * ORGANIZACAO_EM_USO`, com a contagem de equipes ativas e leads em aberto, se houver
   * algo dependendo dela.
   */
  desativar(id: number) {
    return api.post<Unidade>(`/unidades/${id}/desativar`)
  },

  reativar(id: number) {
    return api.post<Unidade>(`/unidades/${id}/reativar`)
  },
}

export const equipesApi = {
  listar(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeOffset<Equipe>>('/equipes', filtros, sinal)
  },

  detalhar(id: number, sinal?: AbortSignal) {
    return api.get<Equipe>(`/equipes/${id}`, undefined, sinal)
  },

  criar(entrada: EntradaCriarEquipe) {
    return api.post<Equipe>('/equipes', entrada)
  },

  atualizar(id: number, entrada: EntradaAtualizarEquipe) {
    return api.patch<Equipe>(`/equipes/${id}`, entrada)
  },

  /**
   * **C3** — designa o responsável da equipe: quem recebe todo lead novo, faz o
   * pré-atendimento e depois atribui manualmente o corretor de plantão.
   *
   * `PUT` porque o corpo é o estado final do campo, e `null` (remover o responsável) é
   * operação legítima, não campo omitido. Exige que a pessoa já seja **membro ativo** da
   * equipe (I11) — caso contrário, `422 RESPONSAVEL_EQUIPE_INVALIDO`.
   */
  definirResponsavel(id: number, responsavelUuid: string | null) {
    return api.put<Equipe>(`/equipes/${id}/responsavel`, { responsavelUuid })
  },

  desativar(id: number) {
    return api.post<Equipe>(`/equipes/${id}/desativar`)
  },

  reativar(id: number) {
    return api.post<Equipe>(`/equipes/${id}/reativar`)
  },

  membros(id: number, sinal?: AbortSignal) {
    return api.get<{ dados: ReadonlyArray<MembroEquipe> }>(
      `/equipes/${id}/membros`,
      undefined,
      sinal,
    )
  },

  /**
   * Alimenta o recarregamento do select "Responsável" quando a equipe muda na TELA-03
   * (FL-04 passo 3) e a atribuição manual ao corretor de plantão (C3). Aplica RN-14 no
   * servidor: ativo + membro da equipe + papel elegível. `paraDistribuicao=true`
   * acrescenta `receberLeads` — a leitura de "está de plantão".
   *
   * A tela **não** filtra a lista de usuários por conta própria: a regra de quem pode
   * receber lead é de negócio e tem de ter uma definição só.
   */
  atendentesElegiveis(id: number, paraDistribuicao = false, sinal?: AbortSignal) {
    return api.get<{ dados: ReadonlyArray<AtendenteElegivel> }>(
      `/equipes/${id}/atendentes-elegiveis`,
      paraDistribuicao ? { paraDistribuicao: 'true' } : undefined,
      sinal,
    )
  },
}

export const usuariosApi = {
  listar(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeOffset<UsuarioNaLista>>('/usuarios', filtros, sinal)
  },

  detalhar(uuid: string, sinal?: AbortSignal) {
    return api.get<UsuarioDetalhado>(`/usuarios/${uuid}`, undefined, sinal)
  },

  criar(entrada: EntradaCriarUsuario) {
    return api.post<UsuarioDetalhado>('/usuarios', entrada)
  },

  atualizar(uuid: string, entrada: EntradaAtualizarUsuario) {
    return api.patch<UsuarioDetalhado>(`/usuarios/${uuid}`, entrada)
  },

  alterarPapel(uuid: string, papel: Papel) {
    return api.patch<UsuarioDetalhado>(`/usuarios/${uuid}/papel`, { papel })
  },

  ativar(uuid: string) {
    return api.post<UsuarioDetalhado>(`/usuarios/${uuid}/ativar`)
  },

  /**
   * O endpoint mais delicado do módulo (RN-07A): sem `destino`, a API responde `409
   * USUARIO_POSSUI_LEADS_EM_ABERTO` com a quantidade em `detalhes[].mensagem`. A tela
   * usa esse `409` como **passo do fluxo**, não como erro: abre a escolha do destino e
   * reenvia.
   *
   * Desativar também revoga todas as sessões (RN-73) e libera as equipes das quais a
   * pessoa era responsável (I11/C3) — os dois vêm na resposta.
   */
  desativar(uuid: string, entrada: EntradaDesativarUsuario) {
    return api.post<ResultadoDesativacaoUsuario>(`/usuarios/${uuid}/desativar`, entrada)
  },

  /** RN-14 / C3 — "está de plantão": entra na lista de destinos da atribuição manual. */
  alternarReceberLeads(uuid: string, receberLeads: boolean) {
    return api.patch<UsuarioDetalhado>(`/usuarios/${uuid}/receber-leads`, { receberLeads })
  },

  definirEquipes(uuid: string, equipeIds: ReadonlyArray<number>) {
    return api.put<UsuarioDetalhado>(`/usuarios/${uuid}/equipes`, { equipeIds })
  },

  definirUnidadesEscopo(uuid: string, unidadeIds: ReadonlyArray<number>) {
    return api.put<UsuarioDetalhado>(`/usuarios/${uuid}/unidades`, { unidadeIds })
  },

  /**
   * Convite com link de definição de senha. É a **única** forma de um gestor mexer na
   * senha de outra pessoa (nota ⁶ de `04-roles §3.5`): definir senha de terceiro é
   * privilégio do Administrador, para que ninguém assuma a identidade de um corretor.
   */
  enviarConvite(uuid: string) {
    return api.post<{ mensagem: string }>(`/usuarios/${uuid}/convite`)
  },

  leadsEmAberto(uuid: string, sinal?: AbortSignal) {
    return api.get<{ total: number }>(`/usuarios/${uuid}/leads-em-aberto`, undefined, sinal)
  },

  /** TELA-26 — "Transferir Leads" / "Mudar Leads de Equipe" da TELA-10. */
  transferirLeads(uuid: string, entrada: EntradaTransferirCarteira) {
    return api.post<ResultadoTransferenciaDeCarteira>(
      `/usuarios/${uuid}/transferir-leads`,
      entrada,
    )
  },
}
